import React, { useState, useEffect, useRef } from 'react';
import './MedicationAutocomplete.css';

interface MedicationAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  name?: string;
}

const MedicationAutocomplete: React.FC<MedicationAutocompleteProps> = ({
  value,
  onChange,
  placeholder = "Ej: Paracetamol, Ibuprofeno...",
  id = "medication",
  name = "medication"
}) => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Lista de medicamentos comunes para dolores de cabeza
  const commonMedications = [
    // Analgésicos básicos
    'Paracetamol 500mg',
    'Paracetamol 650mg',
    'Paracetamol 1000mg',
    
    // Antiinflamatorios (NSAIDs)
    'Ibuprofeno 400mg',
    'Ibuprofeno 600mg',
    'Ibuprofeno 800mg',
    'Naproxeno 220mg',
    'Naproxeno 440mg',
    'Diclofenaco 50mg',
    'Diclofenaco 75mg',
    
    // Combinaciones comunes
    'Paracetamol + Cafeína',
    'Ibuprofeno + Paracetamol',
    'Ácido Acetilsalicílico 500mg',
    'Ácido Acetilsalicílico + Cafeína',
    
    // Medicamentos específicos para migraña
    'Sumatriptan 50mg',
    'Sumatriptan 100mg',
    'Rizatriptan 10mg',
    'Zolmitriptan 2.5mg',
    'Zomig 2.5mg',
    'Zomig 5mg',
    'Almotriptan 12.5mg',
    'Eletriptan 20mg',
    'Eletriptan 40mg',
    'Frovatriptan 2.5mg',
    'Naratriptan 2.5mg',
    
    // Medicamentos más fuertes (con receta)
    'Metamizol 500mg',
    'Tramadol 50mg',
    'Codeína + Paracetamol',
    
    // Relajantes musculares
    'Relajante muscular',
    'Carisoprodol 350mg',
    
    // Medicamentos naturales/alternativos
    'Magnesio',
    'Vitamina B2',
    'Feverfew (Matricaria)',
    'Jengibre',
    
    // Marcas comerciales comunes (España)
    'Gelocatil',
    'Espidifen',
    'Nolotil',
    'Enantyum',
    'Voltaren',
    'Frenadol',
    'Dolocatil',
    'Algidol'
  ];

  // Filtrar sugerencias basadas en el input
  const filterSuggestions = (input: string) => {
    if (!input.trim()) return [];
    
    const filtered = commonMedications.filter(medication =>
      medication.toLowerCase().includes(input.toLowerCase())
    );
    
    // Ordenar por relevancia: primero las que empiezan con el texto, luego las que lo contienen
    return filtered.sort((a, b) => {
      const aStarts = a.toLowerCase().startsWith(input.toLowerCase());
      const bStarts = b.toLowerCase().startsWith(input.toLowerCase());
      
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      
      return a.length - b.length; // Más cortas primero
    }).slice(0, 8); // Máximo 8 sugerencias
  };

  // Manejar cambios en el input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    const filtered = filterSuggestions(newValue);
    setSuggestions(filtered);
    setShowSuggestions(filtered.length > 0 && newValue.length > 0);
    setActiveSuggestion(-1);
  };

  // Manejar navegación con teclado
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveSuggestion(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        setActiveSuggestion(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
        
      case 'Enter':
        e.preventDefault();
        if (activeSuggestion >= 0 && activeSuggestion < suggestions.length) {
          selectSuggestion(suggestions[activeSuggestion]);
        }
        break;
        
      case 'Escape':
        setShowSuggestions(false);
        setActiveSuggestion(-1);
        break;
    }
  };

  // Seleccionar una sugerencia
  const selectSuggestion = (suggestion: string) => {
    onChange(suggestion);
    setShowSuggestions(false);
    setActiveSuggestion(-1);
    inputRef.current?.focus();
  };

  // Manejar focus/blur
  const handleFocus = () => {
    if (value.length > 0) {
      const filtered = filterSuggestions(value);
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    }
  };

  const handleBlur = () => {
    // Delay para permitir clicks en sugerencias
    setTimeout(() => {
      setShowSuggestions(false);
      setActiveSuggestion(-1);
    }, 150);
  };

  // Scroll automático para el elemento activo
  useEffect(() => {
    if (activeSuggestion >= 0 && suggestionsRef.current) {
      const activeElement = suggestionsRef.current.children[activeSuggestion] as HTMLElement;
      if (activeElement) {
        activeElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [activeSuggestion]);

  return (
    <div className="medication-autocomplete">
      <input
        ref={inputRef}
        type="text"
        id={id}
        name={name}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        autoComplete="off"
        className="medication-input"
      />
      
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="suggestions-list"
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion}
              className={`suggestion-item ${index === activeSuggestion ? 'active' : ''}`}
              onClick={() => selectSuggestion(suggestion)}
            >
              <span className="medication-icon">💊</span>
              <span className="medication-name">{suggestion}</span>
            </div>
          ))}
          
          <div className="suggestions-footer">
            <small>💡 Usa ↑↓ para navegar, Enter para seleccionar</small>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicationAutocomplete;